import {
  Button,
  ContentReferences,
  DeleteConfirmButton,
  DevDetailsButton,
  Drawer,
  DrawerProps,
  Flex,
  Menu,
  Spacer,
  Text,
} from 'components'
import { GlobeAltIcon, PencilIcon } from 'components/icons'
import { useAlert } from 'components/providers'
import { isEqual } from 'lodash-es'
import { useEffect, useState } from 'react'
import { ITrait, TraitWrapper } from 'types'
import { useTheme } from 'utils'
import { hideTraitValues } from '../../utils'
import ManageTrait from './ManageTrait'
import ManageTraitValue from './ManageTraitValue'
import TraitValueList from './TraitValueList'

interface Props<T extends TraitWrapper | ITrait> extends Omit<DrawerProps, 'children' | 'onClose'> {
  trait?: T
  onDelete?: () => void
  onDone: (trait?: T) => void
  /** Add a SplitButton that adds a save all option */
  onDoneAll?: (trait?: T) => void
  /** Add a SplitButton that resets the trait to the shared trait */
  onReset?: () => void
}

export default function ManageTraitDrawer<T extends TraitWrapper | ITrait>({
  trait,
  onDelete,
  onDone,
  onDoneAll,
  onReset,
  ...rest
}: Props<T>) {
  const { theme } = useTheme()
  const { confirm } = useAlert()

  const [draft, setDraft] = useState<T>()
  const [initialDraft, setInitialDraft] = useState<T>()
  const [selectedValueIndex, setSelectedValueIndex] = useState<number>()

  useEffect(() => {
    if (!trait) return

    setDraft(trait)
    setInitialDraft(trait)
  }, [trait])

  const isDirty = !isEqual(draft, initialDraft)
  if (!draft) return null

  const { id, title, values } = draft
  const selectedValue = selectedValueIndex !== undefined ? values?.[selectedValueIndex] : undefined

  function update(body: Partial<ITrait>) {
    setDraft(prev => prev && { ...prev, ...body })
  }

  async function handleDone() {
    if (selectedValueIndex != null) return setSelectedValueIndex(undefined)
    onDone(draft)
  }

  async function handleCancel() {
    if (selectedValueIndex != null) return setSelectedValueIndex(undefined)

    if (isDirty) {
      const confirmed = await confirm('Discard changes?')
      if (!confirmed) return
    }
    onDone()
  }

  return (
    <Drawer
      onClose={handleCancel}
      headerIcon={<PencilIcon />}
      header={
        <Flex alignItems="center">
          Edit&nbsp;
          {id && !onReset && (
            <Flex alignItems="center" gap={1} color="primary.500">
              <GlobeAltIcon type="outline" />
              Shared&nbsp;
            </Flex>
          )}
          Trait
        </Flex>
      }
      headerActions={
        <>
          {id && (
            <DevDetailsButton items={[{ collection: 'traits2', id, label: 'Trait', type: 'db' }]} />
          )}
          {onDelete && (
            <Menu
              _hover={{ color: theme === 'light' ? 'primary.500' : undefined }}
              disabled={!!selectedValueIndex}
              buttonLabel="Actions"
              variant="ghost"
              menuProps={{ positioning: { placement: 'bottom-start' } }}
              options={[
                {
                  type: 'custom',
                  label: (
                    <DeleteConfirmButton
                      onDelete={onDelete}
                      width="100%"
                      borderRadius="none"
                      description="This will remove the trait from the workspace."
                    />
                  ),
                },
              ]}
            />
          )}
        </>
      }
      footer={
        <>
          <Button onClick={handleCancel} variant="outline">
            Cancel
          </Button>
          <Spacer />
          <Button
            disabled={!title?.en || selectedValue?.variants?.some(v => !v.variantTitle)}
            palette="primary"
            onClick={handleDone}
            moreOptions={[
              ...(onDoneAll && selectedValueIndex == null
                ? [
                    {
                      disabled: !isDirty,
                      label: `${id ? 'Update' : 'Create'} Shared Trait`,
                      onClick: () => onDoneAll(draft),
                    },
                  ]
                : []),
              ...(onReset ? [{ label: 'Reset to Shared Trait', onClick: onReset }] : []),
            ]}
          >
            {isDirty && selectedValueIndex == null && id ? 'Save' : 'Done'}
          </Button>
        </>
      }
      {...rest}
    >
      <Flex direction="column" gap={4}>
        {selectedValue ? (
          <>
            <Flex justify={'center'} gap={1}>
              <Text textStyle="bodySmall" opacity={0.7} color={'rgba(23, 23, 23, 0.6)'}>
                Sub content of
              </Text>
              <Text
                textStyle="bodySmall"
                opacity={0.7}
                color={'rgba(23, 23, 23, 0.6)'}
                as="u"
                cursor="pointer"
                onClick={() => setSelectedValueIndex(undefined)}
              >
                {title?.en}
              </Text>
            </Flex>

            <ManageTraitValue
              value={selectedValue}
              onChange={value =>
                update({ values: values?.map((v, i) => (i === selectedValueIndex ? value : v)) })
              }
            />
          </>
        ) : (
          <>
            <ManageTrait value={draft} onChange={update} />
            {!hideTraitValues(draft.leadField) && (
              <TraitValueList
                values={values || []}
                onChange={values => update({ values })}
                onSelect={setSelectedValueIndex}
              />
            )}
            {id && <ContentReferences id={id} type="traits2" />}
          </>
        )}
      </Flex>
    </Drawer>
  )
}
